import { useEffect, useMemo, useState } from 'react';
import {
  LIGHTNING_CAVEAT,
  LIGHTNING_RETENTION_MIN,
  LIGHTNING_SOURCE_LABEL,
  formatLightningDistance,
  lightningBand,
  LIGHTNING_BAND_LABELS,
  nearestFlash,
} from '@ww/shared';
import type { Asset, LightningFlash } from '@ww/shared';
import { I, Icon } from '../lib/icons';

/**
 * How close the lightning is, per asset.
 *
 * This is the number behind a real decision — pulling a crew off a field, holding a
 * ballgame — so it is deliberately coarse. GLM locates a flash to about ten kilometres;
 * printing "6.2 mi" would dress that up as a precision the instrument does not have,
 * which is how an operator ends up trusting a figure further than it deserves.
 */

export default function LightningCard({
  flashes,
  assets,
  onClose,
}: {
  flashes: LightningFlash[];
  assets: Asset[];
  onClose: () => void;
}) {
  // Ages are on screen; without a tick "3 min ago" quietly becomes a lie.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const rows = useMemo(() => {
    const enabled = assets.filter((a) => a.enabled);
    return enabled
      .map((a) => ({ asset: a, near: nearestFlash(flashes, a.location.coordinates, now) }))
      .filter((r): r is { asset: Asset; near: NonNullable<ReturnType<typeof nearestFlash>> } => !!r.near)
      .sort((x, y) => x.near.miles - y.near.miles);
  }, [flashes, assets, now]);

  return (
    <div className="panel side-card">
      <div className="panel-head">
        <Icon d={I.bolt} size={16} />
        <span className="lbl" style={{ flexGrow: 1 }}>
          Lightning
        </span>
        <button type="button" className="chip icon-chip hov" aria-label="Close lightning panel" onClick={onClose}>
          <Icon d={I.x} size={14} />
        </button>
      </div>

      <div className="side-card-body">
        {flashes.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No lightning detected in the last {LIGHTNING_RETENTION_MIN} minutes.
          </div>
        ) : (
          <>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {flashes.length} flash{flashes.length === 1 ? '' : 'es'} in the last {LIGHTNING_RETENTION_MIN} min
            </div>

            {rows.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>
                No enabled assets to measure from.
              </div>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {rows.slice(0, 6).map(({ asset, near }) => {
                  const band = lightningBand(near.miles);
                  return (
                    <div key={asset._id} className="ltg-row">
                      <span style={{ minWidth: 0 }}>
                        <span className="ltg-asset">{asset.name}</span>
                        <br />
                        <span className="ltg-age">
                          {near.minutesAgo <= 1 ? 'just now' : `${near.minutesAgo} min ago`}
                        </span>
                      </span>
                      <span style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span className={`ltg-dist ltg-band-${band}`}>
                          {formatLightningDistance(near.miles)}
                        </span>
                        <br />
                        <span className="ltg-age">{LIGHTNING_BAND_LABELS[band]}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/*
          Always shown, whether or not there are flashes. A reader who sees this panel
          empty and later sees it full should have been told both times what the data is.
        */}
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.45, borderTop: '1px solid #1e2c46', paddingTop: 8 }}>
          {LIGHTNING_SOURCE_LABEL}. {LIGHTNING_CAVEAT}
        </div>
      </div>
    </div>
  );
}
